import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import NextAction from '../Widget/NextAction';
import Overlay from '../Widget/Overlay';
import OverlayPartner from '../Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import WidgetHeader from '../Widget/WidgetHeader';

import { Container, Body } from '../DefaultStyles/Details';
import {
    Footer,
    SpinnerContainer,
    ScrollNotice,
    AutoScalingTitle,
    AutoScalingDescription
} from '../DefaultStyles/Shared';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import { volunteerMatchLogoURL } from '../../components/Volunteer/Shared';
import CssTransition from '../../components/Transition/CssTransition';
import DefaultVolunteerImage from '../../components/Volunteer/DefaultImage';
import LocationChangePopup from '../../components/Volunteer/LocationChangePopup';
import ShareDrawer from '../../components/ShareDrawer';
import VolunteerSubscribeForm from '../../components/Volunteer/VolunteerSubscribeForm';

import {
    normalizeOpportunityName,
    replaceLocale,
} from '../../utils/volunteer/helpers';

import multiLineElipsis, { styledScrollbar } from '../../components/Shared/mixins';

import {
    getApiRequestData,
    getExitingCardProps,
    onBackWithinCard,
    onGoToNextActionInsideCard,
    onSubscribeComplete,
    setExitingCardProps,
    setOverlayPartnerVisible,
    setPartner
} from '../Widget/actions';
import OverlayVolunteer from '../Widget/OverlayVolunteer';
import {
    fetchVolunteerOpportunities,
    onDetailsContainerClick,
    onSelectVolunteerOpportunity,
    onShowLocationChangePopup,
    toggleOverlayVolunteerVisibility,
    useBackAnimations
} from './actions';

import { cacheFormData } from '../../utils/formCaching/actions';
import { ActionTypes, CardTypes } from '../../utils/enums';
import { isPortrait } from '../../utils/size';
import { Placeholders } from '../../utils/fillDynamicText';
import { ThemeConsumer } from '../../utils/theming';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';

const OpportunityHeader = styled.header`
    display: flex;
`;

const OpportunityTitle = styled.h3`
    font-size: 16px;
    margin: 0;
    margin-bottom: 3px;
    word-break: break-letter;
    hyphens: auto;
    ${() => multiLineElipsis(1.35, isPortrait() ? 3 : 2)}
`;

const Description = styled.p`
    font-family: Lato;
    margin: 0;
    line-height: 1.33;
    font-size: ${() => (isPortrait() ? 14 : 12)}px;
    overflow: hidden;
    color: #000;
    word-break: break-word;
    word-break: break-letter;
    hyphens: auto;
    display: -webkit-box;
    ${() => multiLineElipsis(1.35, isPortrait() ? 4 : 2)}

    span {
        font-weight: 800;
        color: ${({ theme }) => theme.colors.primary};
        cursor: pointer;
    }
`;

const PartnerImage = styled.img`
    max-height: ${() => (isPortrait() ? 45 : 60)}px;
    max-width: 60px;
    object-fit: contain;
`;

const VolunteerWrapper = styled.div`
    display: flex;
    flex: 1;
    flex-direction: ${() => (isPortrait() ? 'column' : 'row')};
    margin-top: 1em;
`;

const VolunteerInfo = styled.div`
    width: 45%;
    margin-right: 20px;
    ${() =>
        isPortrait() &&
        `
        margin-right: 0;
        width: 100%;
        margin-bottom: 24px;
    `};
`;
const VolunteerOpportunities = styled.div`
    width: 310px;
    padding-right: ${({ length }) => (length > 1 ? 5 : 0)}px;
    overflow-y: scroll;
    position: relative;
    ${({ theme }) => styledScrollbar(theme.colors.primary)};
    ${() => isPortrait() && `flex: 1; width: 100%;`};

    > div,
    > form {
        padding: 12px;
        box-shadow: 0 2px 4px 0 rgba(228, 228, 228, 0.5);
        border: solid 1px #dadad9;
        margin-bottom: 10px;
        position: relative;
    }
`;

const DetailsColumn = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-left: 12px;
`;

const DetailsButton = styled.button`
    cursor: pointer;
    border: solid 1px #f1f1ee;
    height: 46px;
    width: 100%;
    font-family: Averta;
    margin-top: 12px;
    padding: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: 1.5px;
    background-color: #fff;
    ${({ theme }) =>
        isPortrait() &&
        `   background-color: ${theme.colors.primary};
            border: solid 1px ${theme.colors.primary};
            color: #fff;
        `};

    &:hover {
        background-color: ${({ theme }) => theme.colors.primary};
        border: ${({ theme }) => `solid 1px ${theme.colors.primary}`};
        color: #fff;
    }

    &:active,
    &:focus {
        outline: none;
    }
`;

const ScrollNoticeContainer = styled.div`
    position: absolute;
    width: 150px;
    cursor: pointer;
    right: 170px;
    bottom: 50px;
    transform: translateX(50%);

    ${() => isPortrait() && `right: 50%;`};

    span {
        padding: 0;
        text-align: center;
        width: 100%;
    }
`;

class VolunteerDetails extends React.Component {
    constructor() {
        super();
        this.state = {
            nextActionVisibile: false,
            emailSignedUp: false,
            hover: true,
            scrolled: false,
            scrollButtonVisible: true
        };
        this.scrollContainer = React.createRef();
    }

    scrollCallback = e => {
        if (e.target.scrollTop > 10) {
            this.setState({ scrolled: true, scrollButtonVisible: false });
        } else {
            this.setState({ scrolled: false, scrollButtonVisible: true });
        }
    };

    handleScrollButtonClick = () => {
        const c = this.scrollContainer.current;
        c.scrollBy({ top: (c.scrollHeight - c.clientHeight) / 2, behavior: 'smooth' });
        this.setState({ scrolled: true });
        setTimeout(() => {
            this.setState({ scrollButtonVisible: false });
        }, 200);
    };

    getScrollPosition = () => {
        if (this.scrollContainer.current) {
            return this.scrollContainer.current.scrollTop;
        }
    };

    componentDidMount = () => {
        this.props.setPartner(this.props.actionIdentifier);

        if (this.props.volunteerScrollPosition && this.scrollContainer.current) {
            this.scrollContainer.current.scrollTop = this.props.volunteerScrollPosition;
        }
        if (this.props.postalCode && !this.props.volunteerOpportunities) {
            this.loadVolunteerOpportunities();
        }
        if (this.props.card.type === CardTypes.actionChoice) {
            setTimeout(() => {
                this.props.useBackAnimations();
            }, 1000);
        }
    };

    loadVolunteerOpportunities = () => {
        this.props.fetchVolunteerOpportunities(
            this.props.postalCode,
            this.props.countryCode,
            this.props.activeActionId,
            this.props.actionIdentifier,
            false, // preload screen
            false, // popup
            false // cache
        );
    };

    buildAddress = (s1 = '', s2 = '', city, state = '', zip = '') =>
        `${s1} ${s2} ${city ? city + ',' : ''} ${state} ${zip}`;

    componentWillUnmount = () => {
        if (this.scrollContainer.current) {
            this.scrollContainer.current.removeEventListener(
                'scroll',
                this.scrollCallback
            );
        }
    };
    render() {
        if (this.scrollContainer.current) {
            this.scrollContainer.current.addEventListener('scroll', this.scrollCallback);
        }
        const sequenceFromDetailsCard =
            this.state.emailSignedUp &&
            this.props.actions.length === 1 &&
            this.props.hasNextAction;
        return (
            <ThemeConsumer>
                {theme => (
                    <Container
                        onClick={() =>
                            this.props.onDetailsContainerClick(
                                this.props.actionIdentifier,
                                this.props.actionInitiated
                            )
                        }
                    >
                        <Overlay />
                        <OverlayPublisher />
                        <OverlayVolunteer transitionType="left-to-right" />
                        <OverlayPartner
                            logoIsSmall={false}
                            transitionType="right-to-left"
                            logo={volunteerMatchLogoURL}
                            name={'About VolunteerMatch'}
                            description={
                                "We believe everyone should have the chance to make a difference. That's why we make it easy for good people and good causes to connect. We've connected millions of people with a great place to volunteer and helped tens of thousands of organizations better leverage volunteers to create real impact."
                            }
                        />
                        <Body>
                            <WidgetHeader />
                            <VolunteerWrapper>
                                <VolunteerInfo>
                                    <AutoScalingTitle
                                        style={{
                                            maxHeight: isPortrait() ? '5rem' :'7rem',
                                            marginBottom: '0.5em'
                                        }}
                                        onClick={e => {
                                            if (e.target.localName !== "a") e.preventDefault(); //don't prevent user from clicking a tags
                                            this.props.setOverlayPartnerVisible(
                                                true,
                                                e,
                                                false
                                            );
                                        }}
                                        dangerouslySetInnerHTML={{__html:this.props.headerText}}
                                    />
                                    <AutoScalingDescription
                                        style={{
                                            height: isPortrait() ? '5rem' :'8.5rem',
                                        }}
                                        onClick={e => {
                                            if (e.target.localName !== "a") e.preventDefault();
                                            this.props.setOverlayPartnerVisible(
                                                true,
                                                e,
                                                false
                                            );
                                        }}
                                        dangerouslySetInnerHTML={{__html:this.props.descriptionText}}
                                    />
                                </VolunteerInfo>
                                {this.props.isVolunteerOpportunitiiesLoading ? (
                                    <SpinnerContainer>
                                        <LoadingSpinner
                                            position={'center'}
                                            width={80}
                                            height={80}
                                            stroke={6}
                                            color={theme.colors.primary}
                                        />
                                    </SpinnerContainer>
                                ) : (
                                    <VolunteerOpportunities
                                        theme={theme}
                                        length={this.props.actions.length}
                                        ref={this.scrollContainer}
                                    >
                                        {this.props.actions.map((a, i) => {
                                            if (a.id === 'signup') {
                                                return (
                                                    <VolunteerSubscribeForm
                                                        key={a.id}
                                                        onSubmit={values => {
                                                            this.props.cacheFormData(
                                                                values
                                                            );
                                                            this.props.onSubscribeComplete(
                                                                values,
                                                                this.props.activeAction
                                                                    .type,
                                                                null,
                                                                null,
                                                                false,
                                                                () => {
                                                                    this.setState({
                                                                        emailSignedUp: true
                                                                    });
                                                                    const nextActionVisibleTimeout = setTimeout(
                                                                        () => {
                                                                            this.setState(
                                                                                {
                                                                                    nextActionVisibile: true
                                                                                }
                                                                            );
                                                                            clearTimeout(
                                                                                nextActionVisibleTimeout
                                                                            );
                                                                        },
                                                                        500
                                                                    );
                                                                }
                                                            );
                                                        }}
                                                        isEmailSubmitting={
                                                            this.props.isEmailSubmitting
                                                        }
                                                        isEmailSubmitted={
                                                            this.state.emailSignedUp
                                                        }
                                                        numActions={
                                                            this.props.actions.length - 1
                                                        }
                                                    />
                                                );
                                            } else {
                                                return (
                                                    <div key={a.title}>
                                                        <OpportunityHeader>
                                                            {a.imageUrl ? (
                                                                <PartnerImage
                                                                    className="ie-object-fit-contain-polyfill"
                                                                    src={decodeURIComponent(
                                                                        a.imageUrl
                                                                    )}
                                                                />
                                                            ) : (
                                                                <DefaultVolunteerImage
                                                                    issueId={
                                                                        this.props
                                                                            .associatedOpportunityIssueId
                                                                    }
                                                                />
                                                            )}
                                                            <DetailsColumn>
                                                                <OpportunityTitle>
                                                                    {normalizeOpportunityName(a.title)}
                                                                </OpportunityTitle>
                                                                <Description
                                                                    theme={theme}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html:
                                                                            a.description
                                                                    }}
                                                                    onClick={e =>
                                                                        this.props.setOverlayPartnerVisible(
                                                                            true,
                                                                            e,
                                                                            false
                                                                        )
                                                                    }
                                                                />
                                                            </DetailsColumn>
                                                        </OpportunityHeader>
                                                        <DetailsButton
                                                            theme={theme}
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                this.props.onSelectVolunteerOpportunity(
                                                                    i,
                                                                    this.props.actions,
                                                                    this.getScrollPosition()
                                                                );
                                                                this.props.toggleOverlayVolunteerVisibility(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            {'GET DETAILS'}
                                                        </DetailsButton>
                                                    </div>
                                                );
                                            }
                                        })}
                                    </VolunteerOpportunities>
                                )}
                            </VolunteerWrapper>
                            {this.props.actions.length > 2 &&
                                this.state.scrollButtonVisible && (
                                    <ScrollNoticeContainer
                                        onClick={this.handleScrollButtonClick}
                                    >
                                        <ScrollNotice theme={theme}>
                                            <span>{'Scroll down for more'}</span>
                                        </ScrollNotice>
                                    </ScrollNoticeContainer>
                                )}
                            <TransitionGroup>
                                {this.props.postalCodeFormPopupIsVisible && (
                                    <CssTransition
                                        key="alert"
                                        in={this.props.postalCodeFormPopupIsVisible}
                                    >
                                        <LocationChangePopup
                                            onBack={() => {
                                                this.props.onShowLocationChangePopup(
                                                    false,
                                                    this.props.actionIdentifier,
                                                    this.props.actionInitiated
                                                );
                                            }}
                                        />
                                    </CssTransition>
                                )}
                            </TransitionGroup>
                            {sequenceFromDetailsCard && this.state.nextActionVisibile && (
                                <NextAction
                                    exitingAction={this.props.activeAction}
                                    beforeAdvancing={() =>
                                        this.props.setExitingCardProps(
                                            'volunteer',
                                            this.props,
                                            this.props.activeAction
                                        )
                                    }
                                />
                            )}
                            <Footer>
                                <ShareDrawer
                                    beforeBack={() => {
                                        this.props.setExitingCardProps(
                                            'volunteer',
                                            this.props,
                                            this.props.activeAction
                                        );
                                    }}
                                    goBack={() => {
                                        return this.props.card.type ===
                                            CardTypes.actionChoice
                                            ? this.props.onBackWithinCard(
                                                  this.props.actionIdentifier,
                                                  this.props.card,
                                                  true
                                              )
                                            : null;
                                    }}
                                    fullwidth={true}
                                    hasBackButton={
                                        this.props.card.type === CardTypes.actionChoice
                                    }
                                    backgroundColor={
                                        sequenceFromDetailsCard ? null : '#ffffff'
                                    }
                                >
                                    <span
                                        onClick={() =>
                                            this.props.onShowLocationChangePopup(
                                                true,
                                                this.props.actionIdentifier,
                                                this.props.actionInitiated
                                            )
                                        }
                                    >
                                        {'Change Location'}
                                    </span>
                                </ShareDrawer>
                            </Footer>
                        </Body>
                    </Container>
                )}
            </ThemeConsumer>
        );
    }
}

VolunteerDetails.propTypes = {
    actionIdentifier: PropTypes.string.isRequired,
    actionInitiated: PropTypes.bool.isRequired,
    actions: PropTypes.array.isRequired,
    activeAction: PropTypes.object.isRequired,
    activeActionId: PropTypes.number.isRequired,
    associatedOpportunityIssueId: PropTypes.number,
    cacheFormData: PropTypes.func.isRequired,
    card: PropTypes.object.isRequired,
    countryCode: PropTypes.string,
    descriptionText: PropTypes.string,
    fetchVolunteerOpportunities: PropTypes.func.isRequired,
    hasNextAction: PropTypes.bool.isRequired,
    headerText: PropTypes.string.isRequired,
    isEmailSubmitting: PropTypes.bool,
    isVolunteerOpportunitiiesLoading: PropTypes.bool,
    onBackWithinCard: PropTypes.func.isRequired,
    onDetailsContainerClick: PropTypes.func.isRequired,
    onSelectVolunteerOpportunity: PropTypes.func.isRequired,
    onShowLocationChangePopup: PropTypes.func.isRequired,
    onSubscribeComplete: PropTypes.func.isRequired,
    postalCode: PropTypes.string,
    postalCodeFormPopupIsVisible: PropTypes.bool.isRequired,
    setExitingCardProps: PropTypes.func.isRequired,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    toggleOverlayVolunteerVisibility: PropTypes.func.isRequired,
    useBackAnimations: PropTypes.func.isRequired,
    volunteerOpportunities: PropTypes.array,
    volunteerScrollPosition: PropTypes.number
};

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'volunteer',
            ['/volunteer', '/volunteer/start'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;
        const { widget } = state;
        const { activeAction } = widget;
        const { volunteer } = state;
        return {
            actions: (volunteer.volunteerOpportunities || [])
                .map(a => ({ ...a }))
                .concat({ id: 'signup' }),
            actionIdentifier: activeAction.identifier,
            actionInitiated: volunteer.actionInitiated,
            activeAction,
            activeActionId: activeAction.id,
            associatedOpportunityIssueId: activeAction.issue.id,
            card: widget.cards[activeAction.order - 1],
            countryCode: widget.endUserLocation
                ? widget.endUserLocation.countryCode
                : 'US',
            endUserLocation: widget.endUserLocation,
            hasNextAction: !!getNextAction(
                widget.actions,
                activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                widget.cards
            ),
            headerText:
                (widget.cards[activeAction.order - 1].heading ||
                    'Volunteer in your community today!'
                ).replace(
                    new RegExp(Placeholders.LocalePlaceholder, 'g'),
                    replaceLocale(state)
                ),
            descriptionText: (activeAction.text || '').replace(
                new RegExp(Placeholders.LocalePlaceholder, 'g'),
                replaceLocale(state)
            ),
            isEmailSubmitting: widget.isEmailSubmitting,
            isMobileResolution: widget.isMobileResolution,
            isMultiActionsCard:
                widget.cards[activeAction.order - 1].type === CardTypes.multipleActions,
            isVolunteerOpportunitiiesLoading: volunteer.isVolunteerOpportunitiiesLoading,
            postalCode: widget.endUserLocation
                ? widget.endUserLocation.postalCode
                : undefined,
            postalCodeFormPopupIsVisible: volunteer.postalCodeFormPopupIsVisible,
            volunteerOpportunities: volunteer.volunteerOpportunities,
            volunteerScrollPosition: volunteer.volunteerScrollPosition
        };
    },
    {
        cacheFormData,
        fetchVolunteerOpportunities,
        toggleOverlayVolunteerVisibility,
        getApiRequestData,
        onBackWithinCard,
        onDetailsContainerClick,
        onGoToNextActionInsideCard,
        onSelectVolunteerOpportunity,
        onShowLocationChangePopup,
        onSubscribeComplete,
        setExitingCardProps,
        setOverlayPartnerVisible,
        setPartner,
        useBackAnimations
    },
    null,
    {
        areStatesEqual: (next, prev) => {
            if (next.widget.activeAction.type !== ActionTypes.Volunteer) return true;

            return next === prev;
        }
    }
)(VolunteerDetails);
